import { BasketInteractionChannelAuditEntity } from '@genial/services';
import {
  BasketAdvisorOfferEntity,
  BasketAdvisorOrderEntity,
  BasketCustomerOfferEntity,
  BasketCustomerOrderEntity,
  BasketMarketOfferEntity,
  ResponsesBasketAdvisorOfferResponse,
  ResponsesBasketAdvisorOrderResponse,
  ResponsesBasketCustomerOrderResponse,
  ResponsesBasketFileRowsResponse,
  ResponsesCoeOfferResponse,
  ResponsesCustomerResponse,
  ResponsesCustomerTagResponse,
  ResponsesTreasuryOfferResponse,
  type ResponsesBasketCustomerOfferOrderResponse,
  type ResponsesBasketFileResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import { ProductsType } from '../../pages/BatchTicket/components/DynamicAsset/constants';
import { BatchTicketAssetsSelection } from '../../pages/BatchTicket/contexts';
import {
  BasketAdvisorListDataItem,
  CustomerItemDataProps,
  CustomerItemTagObject,
  type totalBasketDeliveredData,
} from '../../pages/BatchTicket/utils/types';
import {
  closedFundForInvestmentStatus,
  normalizeDirectTreasuryOffers,
  normalizeFixedIncomeOffers,
  normalizeFundsOffers,
} from '../product/normalizer';
import { normalizeCoesOffers } from '../product/normalizer/coes';
import { AssetSelectedTypeMap } from '../product/types';

import { getCommonRequireOrderOriginError, messageError } from './constants';

export type FieldType = (typeof fieldsTransform)[keyof typeof fieldsTransform];

type BasketMarketOfferMap = {
  [K in FieldType]: {
    items: BasketAdvisorOfferEntity[];
  };
};

export const normalizeCustomerData = (data?: ResponsesCustomerResponse[] | null) =>
  data?.map((item) => ({
    ...item.customer,
    name: item.customer.name ?? messageError,
    cpfCnpjFormatted: item.customer.cpfCnpjFormatted ?? messageError,
    accountNumber: item.customer.accountNumber ?? messageError,
    suitabilityProfileName: item.customer.suitabilityProfileName ?? messageError,
    registrationStatusName: item.customer.registrationStatusName ?? messageError,
    availableBalanceFormatted: Utils.Mask.Money(item.customer.availableBalance ?? 0),
    tags: (item.customer.tags as CustomerItemTagObject[]) ?? [],
  })) || [];

export const normalizeTagData = (
  data?: ResponsesCustomerTagResponse[] | null
): CustomerItemTagObject[] => {
  if (!data) {
    return [];
  }

  return data.map((item) => ({
    tagId: item.tag.tagId ?? messageError,
    tagName: item.tag.tagName ?? messageError,
    enabled: item.tag.enabled ?? messageError,
  }));
};

export const normalizeCustomerBasketAdvisorData = (
  data: ResponsesBasketCustomerOrderResponse[]
): CustomerItemDataProps[] => {
  const basketCustomerOrderData = data?.map((element) => element.basketCustomerOrder);

  const customerListBasketOrderData = basketCustomerOrderData?.map((basketOrderItem) => ({
    basketCustomerOrderId: basketOrderItem.basketCustomerOrderId,
    availableBalanceFormatted: Utils.Mask.Money(basketOrderItem.customer.account.availableBalance),
    ...basketOrderItem.customer.account,
    ...basketOrderItem.customer,
    name: basketOrderItem.customer.customerName,
    cpfCnpjFormatted: Utils.Mask.CpfOrCnpj(`${basketOrderItem.customer.customerCpfCnpj}`),
    tag: basketOrderItem.customer.hashtags,
  }));

  return customerListBasketOrderData as unknown as CustomerItemDataProps[];
};

export const fieldsTransform = {
  FixedIncome: 'fixedIncome',
  Equity: 'equity',
  Fund: 'funds',
  Treasury: 'directTreasury',
  COE: 'coes',
} as const;

const normalizeOffersBasket = (basketMarketsOrderData: BasketMarketOfferMap) => {
  const normalizeDefaultFields = (item: BasketAdvisorOfferEntity) => ({
    basketOfferId: item.basketAdvisorOfferId,
    productIdNormalized: item.productId,
    orderValue: item.amount,
    inputApplicationAmountNormalized: Utils.Mask.Money(item.amount),
    orderQuantity: item.quantity,
  });

  return {
    fixedIncome: {
      items: basketMarketsOrderData?.fixedIncome?.items?.map((fixedIncomeBasketItem) => {
        const offerNormalized = normalizeFixedIncomeOffers([fixedIncomeBasketItem])[0];
        return {
          ...offerNormalized,
          ...fixedIncomeBasketItem.offer,
          ...normalizeDefaultFields(fixedIncomeBasketItem),
          orderFee: offerNormalized.distributionFeesNormalized.find(
            (fee) => fee.feeId === fixedIncomeBasketItem?.requestedOfferDetail?.feeId
          ),
        } as AssetSelectedTypeMap['fixedIncome'];
      }),
    },
    funds: {
      items: basketMarketsOrderData?.funds?.items?.map((fundBasketOffer) => {
        const minimumMovimentationNormalized = fundBasketOffer.offer.participateMinimumAmount;

        return {
          ...normalizeFundsOffers([fundBasketOffer])[0],
          ...fundBasketOffer.offer,
          ...normalizeDefaultFields(fundBasketOffer),
          closedFund: closedFundForInvestmentStatus.includes(
            fundBasketOffer.offer.fundStatusId || 0
          ),
          closedFundNormalized: closedFundForInvestmentStatus.includes(
            fundBasketOffer.offer.fundStatusId || 0
          ),
          hasPosition: false,
          minimumMovimentationNormalized,
          minimumMovimentationFormatted: Utils.Mask.Money(minimumMovimentationNormalized),
          isReaplicationNormalized: !!false,
        };
      }),
    },
    equity: {
      items: basketMarketsOrderData?.equity?.items.map((equityBasketOffer) => ({
        ...equityBasketOffer.offer,
        ...normalizeDefaultFields(equityBasketOffer),
        priceNormalized: equityBasketOffer.amount,
        quote: equityBasketOffer.offer,
      })),
    },
    directTreasury: {
      items: basketMarketsOrderData?.directTreasury?.items.map((directTreasuryBasketOffer) => ({
        ...normalizeDirectTreasuryOffers([
          directTreasuryBasketOffer as unknown as ResponsesTreasuryOfferResponse,
        ])[0],
        ...directTreasuryBasketOffer.offer,
        ...normalizeDefaultFields(directTreasuryBasketOffer),
      })),
    },
    coes: {
      items: basketMarketsOrderData.coes.items.map((coesBasketOffer) => ({
        ...normalizeCoesOffers([coesBasketOffer as unknown as ResponsesCoeOfferResponse])[0],
        ...coesBasketOffer.offer,
        ...normalizeDefaultFields(coesBasketOffer),
        maximumApplication: 1000000,
        maximumApplicationNormalized: 1000000,
        minimumApplicationNormalized: coesBasketOffer.offer.minimumApplication || 0,
      })),
    },
  } as unknown as BatchTicketAssetsSelection;
};

export const normalizeBasketAdvisorOrderData = (
  basketAdvisorOrder: BasketAdvisorOrderEntity & totalBasketDeliveredData
): BasketAdvisorListDataItem => ({
  ...basketAdvisorOrder,
  totalRequestedAmountFormatted: Utils.Mask.Money(basketAdvisorOrder.totalRequestedAmount || 0),
  applicationType: basketAdvisorOrder.side === 'C' ? 'APLICAÇÃO' : 'RESGATE',
  createDateFormatted: basketAdvisorOrder.createDate
    ? Utils.Mask.DateFormat(basketAdvisorOrder.createDate, 'dd/MM/yyyy')
    : messageError,
  updateDateFormatted: basketAdvisorOrder.updateDate
    ? Utils.Mask.DateFormat(basketAdvisorOrder.updateDate, 'dd/MM/yyyy HH:mm:ss')
    : messageError,
});

export const normalizeBasketAdvisorListData = (
  data?: ResponsesBasketAdvisorOrderResponse[] | null
): BasketAdvisorListDataItem[] =>
  data?.map((item) => ({
    ...normalizeBasketAdvisorOrderData({
      ...item.basketAdvisorOrder,
      totalBasketDeliveredAmount: item.totaBasketDeliveredAmount,
      totalBasketDeliveredOffers: item.totaBasketDeliveredOffers,
      totalBasketDeliveredCustomers: item.totaBasketDeliveredOffers,
    }),
    requestUserFullName: item.requestUserFullName,
  })) || [];

export const normalizeProductsBasketAdvisorData = (
  data: ResponsesBasketAdvisorOfferResponse[]
): BatchTicketAssetsSelection => {
  const basketProductsOrderData = data?.map((element) => element.basketOffer);

  const basketMarketsOrderData = basketProductsOrderData?.reduce(
    (acc, item) => {
      const { marketType } = item;
      const translatedMarketType = fieldsTransform[marketType!] || marketType;

      if (!acc[translatedMarketType!]) {
        acc[translatedMarketType!] = { items: [] };
      }

      acc[translatedMarketType!].items.push(item);

      return acc;
    },
    {
      coes: { items: [] },
      equity: { items: [] },
      fixedIncome: { items: [] },
      funds: { items: [] },
      directTreasury: { items: [] },
    } as BasketMarketOfferMap
  );

  const offersBasketNormalized = normalizeOffersBasket(basketMarketsOrderData);

  return offersBasketNormalized;
};

export const normalizeBasketCustomerOrderItemResumeData = (
  data?: ResponsesBasketCustomerOrderResponse[] | null
) => data?.map((item) => item.basketCustomerOrder) || [];

const verifyOrderOriginAndUpdateErrors = (
  marketType: BasketMarketOfferEntity['marketType'],
  errors: BasketCustomerOfferEntity['errors'],
  productRequireOrigin: (productType: ProductsType) => boolean,
  customerOrderInteractionChannel?: BasketInteractionChannelAuditEntity
) => {
  if (!marketType) {
    return errors;
  }

  const productType = fieldsTransform[marketType] as ProductsType;
  const isRequiredOrderOrigin = productRequireOrigin(productType);

  if (!isRequiredOrderOrigin || !!customerOrderInteractionChannel) {
    return errors;
  }

  const originError = getCommonRequireOrderOriginError();

  return errors ? [...errors, originError] : [originError];
};

export const normalizeAndVerifyBasketCustomerOrderItemResumeData = (
  data: ResponsesBasketCustomerOrderResponse[] | null | undefined,
  productRequireOrigin: (productType: ProductsType) => boolean
) => {
  const normalizedData = normalizeBasketCustomerOrderItemResumeData(data);

  return normalizedData.map((customerOrder) => ({
    ...customerOrder,
    customerOffers: customerOrder.customerOffers?.map((customerOffer) => ({
      ...customerOffer,
      basketOffers: customerOffer.basketOffers?.map((basketOffer) => ({
        ...basketOffer,
        errors: verifyOrderOriginAndUpdateErrors(
          customerOffer.marketType,
          basketOffer.errors || [],
          productRequireOrigin,
          customerOrder.interactionChannelAudit
        ),
      })),
    })),
  }));
};

const filterOffersByErrors = (
  offers: BasketCustomerOfferEntity[] | undefined,
  hasErrors: boolean
): BasketCustomerOfferEntity[] =>
  offers?.filter((offer) =>
    hasErrors
      ? offer.errors && offer.errors.length > 0
      : !offer.errors || (offer.errors && offer.errors.length === 0)
  ) || [];

const filterCustomersByValidOffers = (
  customerOffers: BasketMarketOfferEntity[] | undefined
): BasketMarketOfferEntity[] =>
  customerOffers?.filter((offer) => offer.basketOffers && offer.basketOffers.length > 0) || [];

const processCustomersBasket = (
  customers: BasketCustomerOrderEntity[],
  hasErrors: boolean
): BasketCustomerOrderEntity[] => {
  return customers
    .map((customer) => {
      const processedCustomerOffers = customer.customerOffers?.map((offer) => ({
        ...offer,
        basketOffers: filterOffersByErrors(offer.basketOffers || [], hasErrors),
      }));

      return {
        ...customer,
        customerOffers: filterCustomersByValidOffers(processedCustomerOffers),
      };
    })
    .filter(
      (customer) =>
        (hasErrors && customer.errors && customer.errors.length > 0) ||
        (customer.customerOffers &&
          customer.customerOffers.length > 0 &&
          (!hasErrors || !customer.errors))
    );
};

export const filterCustomersWithOffersThatNeedAttention = (
  customers: BasketCustomerOrderEntity[]
): BasketCustomerOrderEntity[] => processCustomersBasket(customers, true);

export const filterCustomersWithErrorFreeOffers = (
  customers: BasketCustomerOrderEntity[]
): BasketCustomerOrderEntity[] => processCustomersBasket(customers, false);

export const normalizeFileTypeDescription = (fileTypeDescription?: string | null): string => {
  if (!fileTypeDescription) {
    return messageError;
  }

  const translations: Record<string, string> = {
    Base: 'Base',
    Imported: 'Importada',
  };

  return translations[fileTypeDescription] || fileTypeDescription;
};

export const normalizeRedeemBatchTicketRequestsData = (data?: ResponsesBasketFileResponse[]) =>
  data?.map((item) => ({
    ...item.basketFile,
    fileTypeDescriptionFormatted: normalizeFileTypeDescription(
      item.basketFile.basketFileType.fileTypeDescription
    ),
    statusUpdatedAtFormatted: Utils.Mask.DateFormat(
      item.basketFile.updateDate || undefined,
      'dd/MM/yyyy HH:mm:ss'
    ),
  })) || [];

export const normalizeBatchTicketRedeemOffers = (
  offers: ResponsesBasketCustomerOfferOrderResponse[]
) => {
  const data = offers.map((offer) => ({
    ...offer.basketOffer,
    id: offer.basketOffer.basketCustomerOfferId,
    sinacorAccount: offer.basketOffer.customer.account.accountNumber || messageError,
    customerName: offer.basketOffer.customer.customerName || messageError,
    productType: offer.productCategory || messageError,
    assetName: offer.basketOffer.offer.productName || messageError,
    ticketAmount: Utils.Mask.Money(offer.basketOffer.requestedAmount),
    // estimatedNetValue: Utils.Mask.Money(offer.basketOffer.amount),
    status: offer.basketOffer.basketOfferStatus,
    customError: offer.basketOffer?.errors?.map((error) => error.errorMessage).join('\n') || '',
  }));
  return data;
};

export const normalizeBatchTicketRedeemRows = (rows: ResponsesBasketFileRowsResponse[]) => {
  const data = rows.map((row) => ({
    ...row,
    id: row.basketFileRowId,
    sinacorAccount: row.accountNumber || messageError,
    customerName: row.customerName || messageError,
    productType: row.marketType || messageError,
    assetName: row.productName || messageError,
    ticketAmount: Utils.Mask.Money(row.orderAmount),
    // estimatedNetValue: Utils.Mask.Money(row.estimatedNetDMinus1),
    status: row.orderStatus,
    customError: '',
  }));
  return data;
};
